<template>
  <div class="tab-item-content">
    <InfoDialog v-model="showModal" :lang="lang" />

    <div v-if="isTranslatableLang" class="translate-helper mb-3">
      <div class="translate-helper__content">
        <div class="translate-helper__info">
          <span class="translate-helper__badge-emoji" aria-hidden="true">✨</span>
          <div class="translate-helper__badge">
            <span class="translate-helper__badge-label">AI</span>
          </div>
          <div class="translate-helper__text" v-html="$t('ARTWORK_UPLOAD.STEP_2_TABS.TRANSLATE_INLINE', uiLocale)"></div>
        </div>
        <div class="translate-helper__actions">
          <tk-btn
            small
            color="primary"
            class="translate-helper__btn"
            :loading="translationLoading[lang]"
            :disabled="isTranslateDisabled"
            @click="translateDescription"
          >
            {{ translateButtonLabel }}
          </tk-btn>
        </div>
      </div>
    </div>

    <!-- Title -->
    <v-text-field
      :label="$t('ARTWORK_UPLOAD.STEP_2_TABS.TITLE', lang)"
      flat
      box
      v-model="form.title[lang]"
      :error-messages="errors['title.'+lang]"
    >
      <template v-slot:append-outer>
        <v-tooltip dark bottom max-width="300px">
          <template v-slot:activator="{ on }">
            <v-icon size="20" color="primary" v-on="on">fas fa-info-circle</v-icon>
          </template>
          <span>{{ $t('TOOLTIP.ARTWORK_UPLOAD_FORM.TITLE') }}</span>
        </v-tooltip>
      </template>
    </v-text-field>

    <div v-html="$t('ARTWORK_UPLOAD.STEP_2_TABS.DESCRIPTION_INFO', lang)"></div>

    <div class="pl-2">
      <v-radio-group v-model="description_alternative">
        <v-layout row wrap>
          <v-flex xs12 sm6 md3 v-for="alt in description_alternatives" :key="alt.value">
            <v-radio :ripple="false" :label="alt.label" :value="alt.value" color="primary" />
          </v-flex>
        </v-layout>
      </v-radio-group>
    </div>

    <!-- Description -->
    <div class="mt-2">
      <v-textarea
        v-model="form.description[lang]"
        persistent-hint
        counter
        flat
        box
        rows="10"
        :error-messages="errors['description.'+lang]"
        :label="$t('ARTWORK_UPLOAD.STEP_2_TABS.DESCRIPTION')"
      />
    </div>
  </div>
</template>

<script>
import { RepositoryFactory } from "@/services/RepositoryFactory";
import InfoDialog from "./InfoDialog.vue";

const artworksRepo = RepositoryFactory.artworks();

export default {
  components: { InfoDialog },
  props: ["form", "errors", "lang"],
  data() {
    return {
      showModal: false,
      translationLoading: {
        en: false,
        se: false,
      },
      translationCooldown: {
        en: 0,
        se: 0,
      },
      translationCooldownTimers: {},
      description_alternative: "",
      description_alternatives: [
        {
          label: `${this.$t("ARTWORK.PAINTINGS")} (${this.$t(
            "TOOLTIP.ARTWORK.HANG",
            this.lang
          )})`,
          value: "AUTOFILL_PANTING_HANG_READY",
        },
        {
          label: `${this.$t("ARTWORK.PAINTINGS")} (${this.$t(
            "TOOLTIP.ARTWORK.NO_HANG",
            this.lang
          )})`,
          value: "AUTOFILL_PANTING_NOT_HANG_READY",
        },
        {
          label: this.$t("ARTWORK_UPLOAD.PRINT_AND_PHOTO", this.lang),
          value: "AUTOFILL_PRINT_AND_PHOTO",
        },
        {
          label: `${this.$t("ARTWORK.SCULPTURE", this.lang)}`,
          value: "AUTOFILL_SCULPTURE",
        },
      ],
    };
  },

  computed: {
    isTranslatableLang() {
      return ["se", "en"].includes(this.lang);
    },
    uiLocale() {
      return this.$i18n.locale || "fi";
    },
    translateCooldownSeconds() {
      return this.translationCooldown[this.lang] || 0;
    },
    isTranslateDisabled() {
      return this.translateCooldownSeconds > 0 || this.translationLoading[this.lang];
    },
    translateButtonLabel() {
      const label = this.$t("COMMON.TRANSLATE", this.uiLocale);

      return this.translateCooldownSeconds ? `${label} (${this.translateCooldownSeconds}s)` : label;
    },
  },

  watch: {
    description_alternative: function (val, oldval) {
      this.fillDescription(val, oldval);
    },
  },

  mounted() {
    this.$nextTick(() => {
      const el = document.getElementById(
        `description-info-dialog-${this.lang}`
      );

      if (el) {
        el.addEventListener("click", (e) => {
          e.stopPropagation();
          this.showModal = true;
        });
      }
    });
  },
  beforeDestroy() {
    Object.values(this.translationCooldownTimers).forEach((timer) => {
      if (timer) {
        clearInterval(timer);
      }
    });
  },
  methods: {
    startTranslationCooldown(target) {
      const duration = 30;

      this.translationCooldown[target] = duration;

      if (this.translationCooldownTimers[target]) {
        clearInterval(this.translationCooldownTimers[target]);
      }

      this.translationCooldownTimers[target] = setInterval(() => {
        const nextValue = this.translationCooldown[target] - 1;

        if (nextValue <= 0) {
          this.translationCooldown[target] = 0;
          clearInterval(this.translationCooldownTimers[target]);
          this.translationCooldownTimers[target] = null;
          return;
        }

        this.translationCooldown[target] = nextValue;
      }, 1000);
    },

    async fillDescription(val, oldVal) {
      if (!this.isDescriptionTextFromTemplate(oldVal)) {
        const confirm = await this.$root.$confirm(
          this.$t("ARTWORK_UPLOAD.STEP_2_TABS.REPLACE_CONTENT_ALERT_TITLE", this.uiLocale),
          this.$t("ARTWORK_UPLOAD.STEP_2_TABS.REPLACE_CONTENT_ALERT_DESC", this.uiLocale)
        );
        if (!confirm) return;
      }

      this.form.description[this.lang] = this.$t(
        `ARTWORK_UPLOAD.STEP_2_TABS.${val}`,
        this.lang
      );
    },

    isDescriptionTextFromTemplate(val) {
      const description = this.form.description[this.lang];
      const template = val
        ? this.$t(`ARTWORK_UPLOAD.STEP_2_TABS.${val}`, this.lang).toString()
        : "";

      const tmpl = template.trim().toLowerCase();
      const formDesc = description ? description.trim().toLowerCase() : "";

      return tmpl === formDesc;
    },

    async translateDescription() {
      const target = this.lang;
      const sourceText = (this.form.description.fi || "").trim();
      const sourceTitle = (this.form.title.fi || "").trim();
      const targetHasContent =
        (this.form.description[target] && this.form.description[target].trim()) ||
        (this.form.title[target] && this.form.title[target].trim());

      if (this.translationLoading[target] || this.translationCooldown[target]) {
        return;
      }

      if (!sourceText || sourceText.length < 20) {
        this.$app.toast(
          this.$t("ARTWORK_UPLOAD.STEP_2_TABS.TRANSLATE_ERROR_NO_SOURCE", this.uiLocale),
          4000,
          "error"
        );
        return;
      }

      if (targetHasContent) {
        const confirm = await this.$root.$confirm(
          this.$t("ARTWORK_UPLOAD.STEP_2_TABS.REPLACE_CONTENT_ALERT_TITLE", this.uiLocale),
          this.$t("ARTWORK_UPLOAD.STEP_2_TABS.TRANSLATE_REPLACE_CONFIRM", this.uiLocale)
        );

        if (!confirm) return;
      }

      this.translationLoading[target] = true;
      this.startTranslationCooldown(target);

      try {
        const [descriptionTranslation, titleTranslation] = await Promise.all([
          artworksRepo.translateDescription(sourceText, target),
          sourceTitle ? artworksRepo.translateDescription(sourceTitle, target) : Promise.resolve(null),
        ]);

        this.form.description[target] = descriptionTranslation;

        if (titleTranslation) {
          this.form.title[target] = titleTranslation;
        }

        this.$app.toast(
          this.$t("ARTWORK_UPLOAD.STEP_2_TABS.TRANSLATE_SUCCESS", this.uiLocale),
          2500,
          "success"
        );
      } catch (error) {
        let message = this.$t(
          "ARTWORK_UPLOAD.STEP_2_TABS.TRANSLATE_ERROR_GENERIC",
          this.uiLocale
        );
        const status = error.status;

        if (error.data) {
          message =
            (error.data.errors &&
              ((error.data.errors.text && error.data.errors.text[0]) ||
                (error.data.errors.target_lang && error.data.errors.target_lang[0]))) ||
            error.data.message ||
            message;
        }

        if (status === 422 || !error.data) {
          this.$app.toast(message, 5000, "error");
        }
      } finally {
        this.translationLoading[target] = false;
      }
    },
  },
};
</script>
<style lang="scss">
.tab-item-content {
  .description-info-dialog {
    text-decoration: underline;
  }

  .translate-helper {
    border: 1px solid #e3e7ef;
    border-radius: 8px;
    padding: 12px;
    background: #fff8e1;

    .translate-helper__content {
      display: flex;
      align-items: center;
      justify-content: space-between;
      flex-wrap: wrap;
      gap: 8px 12px;
    }

    .translate-helper__info {
      display: flex;
      align-items: center;
      gap: 8px;
      min-width: 0;
      flex: 1 1 260px;
    }

    .translate-helper__badge {
      display: inline-flex;
      align-items: center;
      justify-content: center;
      padding: 6px 12px;
      border-radius: 999px;
      background: #111;
      color: #fff;
      font-weight: 700;
      letter-spacing: 0.02em;
      box-shadow: inset 0 0 0 1px #000;
      white-space: nowrap;
    }

    .translate-helper__badge-emoji {
      display: inline-block;
      font-size: 1.1rem;
    }

    .translate-helper__badge-label {
      font-size: 0.85rem;
      line-height: 1;
    }

    .translate-helper__text {
      font-size: 0.9rem;
      color: #555;
      line-height: 1.3;
      margin: 0;
    }

    .translate-helper__btn {
      white-space: nowrap;
    }

    .translate-helper__actions {
      display: flex;
      align-items: center;
      justify-content: flex-end;
      gap: 8px;
      flex-wrap: wrap;
    }
  }
}

</style>
